/* smart-pet-reminders.js — vanilla progressive enhancement for the Smart Pet Care Reminders page.
 * Replaces the framework runtime from the React prototype:
 *   1. the interactive reminders board (#sn-smart-pet-reminders [data-sn-widget="RemindersBoard"])
 *        — pick a pet, toggle reminders on/off, watch the phone show due notifications,
 *          tap "Mark done" to reschedule a task to its next cycle.
 * No dependencies. Self-contained. Safe to load with `defer`.
 *
 * The FAQ accordion is handled by the shared converted/_assets/sn-accordion.js.
 * Answers are rendered into the static body.html (for SEO/crawlability), so this
 * file only owns the reminders board widget.
 *
 * The page renders a faithful static snapshot server-side; this script enhances it.
 * If the script never runs, the static board + FAQ questions are still visible.
 */
(function () {
  'use strict';

  // <html lang> comes from request.locale.iso_code, so /de/ pages report "de".
  var SN_DE = typeof document !== 'undefined' && document.documentElement
    ? /^de/i.test(document.documentElement.getAttribute('lang') || '')
    : false;
  var T = function (en, de) { return SN_DE ? de : en; };

  // ---- Board data (ported verbatim from RemindersBoard.jsx) -----------------
  // dueIn = days from now (negative = overdue)
  var RB_PETS = {
    Bandit: [
      { id: 'flea', name: T('Flea & tick treatment', 'Floh- & Zeckenschutz'), icon: 'droplet', cycle: T('Monthly', 'Monatlich'), cycleDays: 30, dueIn: 0 },
      { id: 'worm', name: T('Worming', 'Wurmkur'), icon: 'pill', cycle: T('Every 3 months', 'Alle 3 Monate'), cycleDays: 90, dueIn: 5 },
      { id: 'vacc', name: T('Booster vaccination', 'Auffrischungsimpfung'), icon: 'syringe', cycle: T('Annual', 'Jährlich'), cycleDays: 365, dueIn: 34 },
      { id: 'groom', name: T('Grooming', 'Fellpflege'), icon: 'scissors', cycle: T('Every 6 weeks', 'Alle 6 Wochen'), cycleDays: 42, dueIn: 12 },
      { id: 'vet', name: T('Dental check-up', 'Zahnkontrolle'), icon: 'heart', cycle: T('Annual', 'Jährlich'), cycleDays: 365, dueIn: 88 },
    ],
    Luna: [
      { id: 'flea', name: T('Flea & tick treatment', 'Floh- & Zeckenschutz'), icon: 'droplet', cycle: T('Monthly', 'Monatlich'), cycleDays: 30, dueIn: -2 },
      { id: 'worm', name: T('Worming', 'Wurmkur'), icon: 'pill', cycle: T('Every 3 months', 'Alle 3 Monate'), cycleDays: 90, dueIn: 18 },
      { id: 'vacc', name: T('Booster vaccination', 'Auffrischungsimpfung'), icon: 'syringe', cycle: T('Annual', 'Jährlich'), cycleDays: 365, dueIn: 6 },
      { id: 'med', name: T('Eye drops (twice daily)', 'Augentropfen (2× täglich)'), icon: 'clock', cycle: T('Daily course', 'Täglich'), cycleDays: 1, dueIn: 0 },
      { id: 'vet', name: T('Vet check-up', 'Tierarztkontrolle'), icon: 'heart', cycle: T('Every 6 months', 'Alle 6 Monate'), cycleDays: 182, dueIn: 40 },
    ],
  };

  function statusOf(d) {
    if (d < 0) return { label: T('Overdue', 'Überfällig'), color: '#C5462E', bg: '#FBD9D0' };
    if (d === 0) return { label: T('Due today', 'Heute fällig'), color: '#E07A3E', bg: '#FFE3CE' };
    if (d <= 7) return { label: T('Due in ' + d + 'd', 'In ' + d + ' T.'), color: '#E8B23A', bg: '#FFF3D4' };
    return { label: T('In ' + d + 'd', 'In ' + d + ' T.'), color: '#5B8C3E', bg: '#E6FFC6' };
  }
  function dueText(d) {
    // ⚠️ German plural: "seit 1 Tag" but "seit 2 Tagen" — not an English -s rule.
    if (d < 0) return SN_DE
      ? 'seit ' + Math.abs(d) + (Math.abs(d) === 1 ? ' Tag' : ' Tagen') + ' überfällig'
      : 'overdue by ' + Math.abs(d) + ' day' + (Math.abs(d) > 1 ? 's' : '');
    if (d === 0) return T('due today', 'heute fällig');
    if (d === 1) return T('due tomorrow', 'morgen fällig');
    return SN_DE ? 'fällig in ' + d + ' Tagen' : 'due in ' + d + ' days';
  }

  // ---- SVG fragments (ported from illustrations.jsx) ------------------------
  var ICON_PATHS = {
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"></path>',
    check: '<path d="M20 6 9 17l-5-5"></path>',
    clock: '<circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path>',
    heart: '<path d="M19 14c1.5-1.5 3-3.4 3-5.5A4.5 4.5 0 0 0 12 5 4.5 4.5 0 0 0 2 8.5c0 2.1 1.5 4 3 5.5l7 7Z"></path>',
    bell: '<path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.7 21a2 2 0 0 1-3.4 0"></path>',
    droplet: '<path d="M12 2.7l5.2 6.6a6.6 6.6 0 1 1-10.4 0Z"></path>',
    pill: '<path d="M10.5 20.5 3.5 13.5a5 5 0 0 1 7-7l7 7a5 5 0 0 1-7 7Z"></path><path d="m8.5 8.5 7 7"></path>',
    syringe: '<path d="m18 2 4 4M15 5 5 15l4 4L19 9M9 11l2 2M6 14l2 2M2 22l3-1"></path>',
    scissors: '<circle cx="6" cy="6" r="3"></circle><circle cx="6" cy="18" r="3"></circle><path d="M20 4 8.1 15.9M14.5 14.5 20 20M8.1 8.1 12 12"></path>',
  };
  function icon(name, size, color) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' + color +
      '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (ICON_PATHS[name] || '') + '</svg>';
  }
  function pawMark(size, color) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="' + color + '" aria-hidden="true">' +
      '<ellipse cx="7" cy="9" rx="2" ry="2.6"></ellipse>' +
      '<ellipse cx="12" cy="7.4" rx="2" ry="2.7"></ellipse>' +
      '<ellipse cx="17" cy="9" rx="2" ry="2.6"></ellipse>' +
      '<path d="M12 11.2c2.7 0 4.8 2 4.8 4.2 0 1.8-1.6 2.8-3.3 2.5-.6-.1-1.2-.1-1.5-.1s-.9 0-1.5.1c-1.7.3-3.3-.7-3.3-2.5 0-2.2 2.1-4.2 4.8-4.2Z"></path></svg>';
  }

  // ---- Tiny DOM helper ------------------------------------------------------
  function h(tag, opts, kids) {
    var e = document.createElement(tag);
    opts = opts || {};
    if (opts.css) e.style.cssText = opts.css;
    if (opts.cls) e.className = opts.cls;
    if (opts.html != null) e.innerHTML = opts.html;
    if (opts.on) for (var ev in opts.on) e.addEventListener(ev, opts.on[ev]);
    if (opts.attrs) for (var a in opts.attrs) e.setAttribute(a, opts.attrs[a]);
    (kids || []).forEach(function (c) {
      if (c == null || c === false) return;
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return e;
  }

  // ---- Reminders board widget -----------------------------------------------
  function initBoard(root) {
    var state = {
      pet: 'Bandit',
      tasksByPet: JSON.parse(JSON.stringify(RB_PETS)),
      off: {},
    };

    var isOn = function (id) { return !state.off[state.pet + id]; };
    var toggle = function (id) {
      var key = state.pet + id;
      state.off[key] = !state.off[key];
      render();
    };
    var setPet = function (p) {
      if (p === state.pet) return;
      state.pet = p;
      render();
    };
    var markDone = function (id) {
      var t = state.tasksByPet[state.pet].filter(function (x) { return x.id === id; })[0];
      if (t) t.dueIn = t.cycleDays; // reschedule to next cycle
      render();
    };

    // --- phone notification card (ported from RB_PhoneNotif) ---
    function phoneNotif(task) {
      return h('div', {
        cls: 'sn-msg-in',
        css: 'background:#fff;border-radius:18px;padding:14px 15px;box-shadow:0 10px 26px -16px rgba(28,25,23,0.5);border:1px solid var(--sn-line)',
      }, [
        h('div', { css: 'display:flex;align-items:center;gap:9px;margin-bottom:9px' }, [
          h('span', { css: 'width:26px;height:26px;border-radius:7px;background:var(--sn-forest);display:flex;align-items:center;justify-content:center;flex-shrink:0', html: pawMark(15, 'var(--sn-lime)') }),
          h('span', { css: 'font-family:var(--sn-display);font-weight:700;font-size:12px' }, ['Supernormal']),
          h('span', { css: 'margin-left:auto;font-size:11px;color:var(--sn-ink-60)' }, ['now']),
        ]),
        h('div', { css: 'display:flex;gap:10px;align-items:flex-start' }, [
          h('span', { css: 'width:32px;height:32px;border-radius:9px;background:var(--sn-lime-soft);display:flex;align-items:center;justify-content:center;flex-shrink:0', html: icon(task.icon, 17, 'var(--sn-forest)') }),
          h('div', { css: 'min-width:0' }, [
            h('div', { css: 'font-size:13.5px;font-weight:600;line-height:1.35' }, [task.name + ' is ' + dueText(task.dueIn)]),
            h('div', { css: 'font-size:12px;color:var(--sn-ink-60);margin-top:1px' }, [state.pet + ' · ' + task.cycle]),
          ]),
        ]),
        h('button', {
          css: 'margin-top:11px;width:100%;cursor:pointer;border:none;border-radius:10px;padding:9px 0;background:var(--sn-forest);color:var(--sn-cream);font-family:var(--sn-display);font-weight:600;font-size:13px;display:inline-flex;align-items:center;justify-content:center;gap:7px',
          html: icon('check', 15, 'var(--sn-lime)') + ' ' + T('Mark done', 'Erledigt'),
          on: { click: function () { markDone(task.id); } },
        }),
      ]);
    }

    function render() {
      var tasks = state.tasksByPet[state.pet];
      var sorted = tasks.slice().sort(function (a, b) { return a.dueIn - b.dueIn; });
      var due = tasks.filter(function (t) { return isOn(t.id) && t.dueIn <= 7; })
        .sort(function (a, b) { return a.dueIn - b.dueIn; });

      // BOARD column
      var petTabs = h('div', { css: 'display:flex;gap:6px;background:var(--sn-cream-2);border-radius:999px;padding:5px' },
        Object.keys(RB_PETS).map(function (p) {
          var active = state.pet === p;
          return h('button', {
            css: 'border:none;cursor:pointer;border-radius:999px;padding:8px 20px;font-family:var(--sn-display);font-weight:600;font-size:14px;' +
              'background:' + (active ? 'var(--sn-forest)' : 'transparent') + ';color:' + (active ? 'var(--sn-cream)' : 'var(--sn-ink-60)'),
            attrs: { 'data-rb-pet': p },
            on: { click: function () { setPet(p); } },
          }, [p]);
        }));

      var boardHead = h('div', { css: 'display:flex;align-items:center;justify-content:space-between;margin-bottom:22px;gap:16px;flex-wrap:wrap' }, [
        h('div', {}, [
          h('span', { cls: 'sn-eyebrow', css: 'color:var(--sn-forest)' }, ['Care schedule']),
          h('h3', { css: 'font-size:21px;margin-top:8px', attrs: { 'data-rb-title': '' } }, [state.pet + '’s reminders']),
        ]),
        petTabs,
      ]);

      var list = h('div', { css: 'display:flex;flex-direction:column;gap:10px' },
        sorted.map(function (t) {
          var on = isOn(t.id);
          var s = statusOf(t.dueIn);
          return h('div', {
            attrs: { 'data-rb-task': t.id },
            css: 'display:flex;align-items:center;gap:14px;padding:14px 18px;border-radius:16px;border:1px solid var(--sn-line);' +
              'background:' + (on ? '#fff' : 'var(--sn-cream)') + ';opacity:' + (on ? '1' : '0.55'),
          }, [
            h('span', { css: 'width:40px;height:40px;border-radius:11px;background:var(--sn-cream-2);display:flex;align-items:center;justify-content:center;flex-shrink:0', html: icon(t.icon, 20, 'var(--sn-forest)') }),
            h('div', { css: 'flex:1;min-width:0' }, [
              h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:15.5px' }, [t.name]),
              h('div', { css: 'font-size:12.5px;color:var(--sn-ink-60)' }, [t.cycle]),
            ]),
            on ? h('span', {
              css: 'font-family:var(--sn-display);font-weight:700;font-size:12px;color:' + s.color + ';background:' + s.bg + ';border-radius:999px;padding:5px 12px;white-space:nowrap',
            }, [s.label]) : null,
            h('button', {
              attrs: { 'aria-pressed': on ? 'true' : 'false', 'data-rb-toggle': t.id },
              css: 'cursor:pointer;border:none;width:44px;height:26px;border-radius:999px;padding:3px;flex-shrink:0;' +
                'background:' + (on ? 'var(--sn-forest)' : 'var(--sn-line)'),
              on: { click: function () { toggle(t.id); } },
            }, [
              h('span', { css: 'display:block;width:20px;height:20px;border-radius:999px;background:#fff;transform:' + (on ? 'translateX(18px)' : 'translateX(0)') + ';box-shadow:0 1px 3px rgba(0,0,0,0.25)' }),
            ]),
          ]);
        }));

      var addBtn = h('button', {
        css: 'margin-top:14px;background:none;border:1.5px dashed var(--sn-line);border-radius:14px;padding:12px 0;width:100%;cursor:pointer;color:var(--sn-forest);font-family:var(--sn-display);font-weight:600;font-size:14px;display:inline-flex;align-items:center;justify-content:center;gap:8px',
        html: icon('bell', 16, 'var(--sn-forest)') + ' Add a custom reminder',
      });

      var boardCol = h('div', { css: 'padding:32px 38px' }, [boardHead, list, addBtn]);

      // PHONE column
      var notifStack = h('div', { css: 'display:flex;flex-direction:column;gap:10px;overflow-y:auto', attrs: { 'data-rb-notifs': '' } },
        due.length === 0
          ? [h('div', {
              css: 'background:rgba(255,248,240,0.1);border:1px solid rgba(255,248,240,0.2);border-radius:16px;padding:20px 16px;text-align:center;color:var(--sn-cream)',
            }, [
              h('span', { html: icon('check', 26, 'var(--sn-lime)') }),
              h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:15px;margin-top:8px' }, ['All caught up']),
              h('div', { css: 'font-size:12.5px;opacity:0.75;margin-top:3px' }, ['Nothing due this week. We’ll nudge you when it is.']),
            ])]
          : due.map(function (t) { return phoneNotif(t); }));

      var phoneScreen = h('div', { css: 'background:linear-gradient(180deg, #2C5440, #224031);border-radius:30px;overflow:hidden;height:540px;position:relative;display:flex;flex-direction:column;padding:44px 14px 18px' }, [
        h('div', { css: 'position:absolute;top:10px;left:50%;transform:translateX(-50%);width:92px;height:22px;background:#000;border-radius:999px;z-index:5' }),
        h('div', { css: 'text-align:center;color:var(--sn-cream);margin-bottom:18px' }, [
          h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:40px;line-height:1' }, ['9:41']),
          h('div', { css: 'font-size:12.5px;opacity:0.7;margin-top:4px' }, ['Today’s nudges for ' + state.pet]),
        ]),
        notifStack,
      ]);

      var phoneCol = h('div', { css: 'background:var(--sn-cream-2);padding:30px 32px;display:flex;flex-direction:column;align-items:center;gap:14px;border-left:1px solid var(--sn-line)' }, [
        h('div', { cls: 'sn-eyebrow', css: 'color:var(--sn-forest)' }, ['The nudge']),
        h('div', { css: 'width:290px;background:#1C1917;border-radius:40px;padding:11px;box-shadow:0 40px 80px -40px rgba(28,25,23,0.6)' }, [phoneScreen]),
        h('p', { css: 'font-size:12px;color:var(--sn-ink-60);text-align:center;max-width:250px' }, [T('Tap “Mark done” - it reschedules to the next cycle automatically.', 'Auf „Erledigt“ tippen - der nächste Termin wird automatisch gesetzt.')]),
      ]);

      var grid = h('div', {
        css: 'display:grid;grid-template-columns:1.4fr 1fr;border-radius:28px;overflow:hidden;border:1px solid var(--sn-line);box-shadow:0 30px 70px -45px rgba(28,25,23,0.5);background:#fff',
      }, [boardCol, phoneCol]);

      root.innerHTML = '';
      root.appendChild(grid);
    }

    render();
  }

  // The FAQ accordion is handled by the shared converted/_assets/sn-accordion.js.
  // Answers are rendered into the static body.html (for SEO/crawlability), so this
  // file only owns the reminders board widget.

  // ---- Boot -----------------------------------------------------------------
  function boot() {
    var page = document.getElementById('sn-smart-pet-reminders');
    if (!page) return;
    var board = page.querySelector('[data-sn-widget="RemindersBoard"]');
    if (board) initBoard(board);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
